import React, { useMemo } from "react";
import { HiOutlineX } from "react-icons/hi";
import { useTranslation } from "react-i18next";

import type { TagStudent } from "types/TagStudent";
import type { Student } from "types/Student";

interface TaggedStudentMarkersProps {
  containerRef: React.RefObject<HTMLDivElement>;
  pictureDimensions: { width: number; height: number };
  transformState: { scale: number; positionX: number; positionY: number };
  taggedStudents: TagStudent[];
  students: Student[];
  highlightedTagId: number | null;
  pendingCoordinates: { x: number; y: number } | null;
  onRemoveTag?: (tagId: number) => void;
}

const markerBaseClass =
  "marker-base absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none";

const TaggedStudentMarkers: React.FC<TaggedStudentMarkersProps> = ({
  containerRef,
  pictureDimensions,
  transformState,
  taggedStudents,
  students,
  highlightedTagId,
  pendingCoordinates,
  onRemoveTag,
}) => {
  const { t } = useTranslation();
  const markers = useMemo(() => {
    const container = containerRef.current;
    if (
      !container ||
      pictureDimensions.width === 0 ||
      pictureDimensions.height === 0
    ) {
      return [] as Array<{
        tag: TagStudent;
        x: number;
        y: number;
        studentName: string;
        highlighted: boolean;
      }>;
    }

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const aspectRatio = pictureDimensions.width / pictureDimensions.height;
    const containerAspectRatio = containerWidth / containerHeight;

    let imageDisplayWidth: number;
    let imageDisplayHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (aspectRatio > containerAspectRatio) {
      imageDisplayWidth = containerWidth;
      imageDisplayHeight = containerWidth / aspectRatio;
      offsetX = 0;
      offsetY = (containerHeight - imageDisplayHeight) / 2;
    } else {
      imageDisplayWidth = containerHeight * aspectRatio;
      imageDisplayHeight = containerHeight;
      offsetX = (containerWidth - imageDisplayWidth) / 2;
      offsetY = 0;
    }

    const { scale = 1, positionX = 0, positionY = 0 } = transformState;

    return taggedStudents
      .map((tag) => {
        const normalizedX = tag.axis_x / pictureDimensions.width;
        const normalizedY = tag.axis_y / pictureDimensions.height;

        const x = normalizedX * imageDisplayWidth * scale + positionX + offsetX;
        const y =
          normalizedY * imageDisplayHeight * scale + positionY + offsetY;

        if (
          x < -100 ||
          y < -100 ||
          x > containerWidth + 100 ||
          y > containerHeight + 100
        ) {
          return null;
        }

        const student =
          students.find((entry) => entry.id === tag.student_id) || tag.student;
        const studentName = student
          ? `${student.last_name} ${student.first_name}`.trim()
          : "";

        const highlighted = highlightedTagId === tag.id;

        return {
          tag,
          x,
          y,
          studentName,
          highlighted,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [
    containerRef,
    pictureDimensions.height,
    pictureDimensions.width,
    taggedStudents,
    transformState,
    highlightedTagId,
    students,
  ]);

  const pendingMarker = useMemo(() => {
    if (!pendingCoordinates) return null;
    const container = containerRef.current;
    if (
      !container ||
      pictureDimensions.width === 0 ||
      pictureDimensions.height === 0
    ) {
      return null;
    }

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const aspectRatio = pictureDimensions.width / pictureDimensions.height;
    const containerAspectRatio = containerWidth / containerHeight;

    let imageDisplayWidth: number;
    let imageDisplayHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (aspectRatio > containerAspectRatio) {
      imageDisplayWidth = containerWidth;
      imageDisplayHeight = containerWidth / aspectRatio;
      offsetX = 0;
      offsetY = (containerHeight - imageDisplayHeight) / 2;
    } else {
      imageDisplayWidth = containerHeight * aspectRatio;
      imageDisplayHeight = containerHeight;
      offsetX = (containerWidth - imageDisplayWidth) / 2;
      offsetY = 0;
    }

    const { scale = 1, positionX = 0, positionY = 0 } = transformState;

    const normalizedX = pendingCoordinates.x / pictureDimensions.width;
    const normalizedY = pendingCoordinates.y / pictureDimensions.height;

    const x = normalizedX * imageDisplayWidth * scale + positionX + offsetX;
    const y = normalizedY * imageDisplayHeight * scale + positionY + offsetY;

    if (
      x < -100 ||
      y < -100 ||
      x > containerWidth + 100 ||
      y > containerHeight + 100
    ) {
      return null;
    }

    return { x, y };
  }, [
    containerRef,
    pendingCoordinates,
    pictureDimensions.height,
    pictureDimensions.width,
    transformState,
  ]);

  return (
    <div className="pointer-events-none absolute inset-0">
      {markers.map(({ tag, x, y, studentName, highlighted }) => (
        <div
          key={tag.id}
          className={markerBaseClass}
          style={{ left: x, top: y, zIndex: highlighted ? 10 : 1 }}
        >
          <div
            className={`flex flex-col items-center gap-1 transition-all duration-300 ease-in-out transform ${
              highlighted ? "scale-110" : "scale-100"
            }`}
          >
            <span
              className={`block rounded-full border-2 border-white shadow-md transition-all duration-300 ease-in-out ${
                highlighted
                  ? "h-4 w-4 bg-emerald-500"
                  : "h-3 w-3 bg-emerald-400"
              }`}
            />
            {studentName && (
              <span
                className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white shadow-md transition-all duration-300 ease-in-out ${
                  highlighted
                    ? "bg-emerald-500 shadow-lg"
                    : "bg-black/60"
                }`}
              >
                {studentName}
                {onRemoveTag && (
                  <button
                    type="button"
                    data-tag-ignore-click
                    className="group/delete pointer-events-auto relative -mr-1 rounded-full p-0.5 hover:bg-white/20 focus:outline-none focus:ring-1 focus:ring-white"
                    aria-label={t(
                      "_tagged_student_panel._remove_student_aria_label",
                      { studentName },
                    )}
                    onClick={() => onRemoveTag(tag.id)}
                  >
                    <HiOutlineX className="h-3.5 w-3.5" />
                    <span className="pointer-events-none absolute -bottom-7 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[10px] font-normal text-white opacity-0 shadow transition-opacity duration-200 invisible group-hover/delete:visible group-hover/delete:opacity-100">
                      {t("_tagged_student_panel._remove_tag_tooltip", {
                        defaultValue: "園児タグ削除",
                      })}
                    </span>
                  </button>
                )}
              </span>
            )}
          </div>
        </div>
      ))}
      {pendingMarker && (
        <div
          className={markerBaseClass}
          style={{ left: pendingMarker.x, top: pendingMarker.y }}
        >
          <div className="flex flex-col items-center gap-1">
            <span className="block h-4 w-4 rounded-full border-2 border-white bg-emerald-500 shadow" />
          </div>
        </div>
      )}
    </div>
  );
};

export default TaggedStudentMarkers;
